import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ConsentManagerProvider, ConsentBanner, ConsentDialog, useConsentManager } from "@c15t/react";
import "@c15t/react/styles.css";
import { X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { generateDeviceId } from "~/api/client";
import { BANNER_UI_SOURCE, consentOptions, DIALOG_UI_SOURCE, NOTICE_VERSION, TRACKING } from "~/lib/consent";
import { loadTrackingVendors } from "~/lib/consent-vendors";
import { setTrackingConsent } from "~/lib/tracking-consent";
import { useUserStore } from "~/lib/stores/user";
import { trackWebEvent } from "~/lib/web-analytics";

/**
 * Bridges c15t consent state to our vendor SDKs and surfaces the GPC confirmation.
 *
 * c15t keeps every category denied until the user chooses and detects the Global Privacy Control
 * signal on its own; this component reacts to that state — it does not decide consent.
 */
function ConsentEffects() {
  const { has, identifyUser } = useConsentManager();
  const trackingGranted = has(TRACKING);
  const userId = useUserStore(state => state.user?._id);

  // Mirrored for the readers that run outside React and cannot call `has` themselves.
  //
  // Layout, not passive: React runs every layout effect in a commit before any passive one, and the
  // first web event of a page load is emitted from a passive effect. Written later, it would reach
  // that event as a refusal the visitor never made.
  useLayoutEffect(() => {
    setTrackingConsent(trackingGranted);
  }, [trackingGranted]);

  // Passive, because it appends scripts and opens connections — work that must not delay the paint.
  useEffect(() => {
    if (trackingGranted) {
      loadTrackingVendors();
    }
  }, [trackingGranted]);

  // Tie every consent record to an identity so it can be produced for a specific person later
  // (brief §5). Logged-out visitors are keyed by the first-party device id; once the user is
  // authenticated we upgrade the same subject to the account id.
  // ponytail: stores the raw account id, not a SHA-256 hash. This deviates from brief §5
  // (user_id_hash) — confirmed acceptable for our internal opaque account id; have legal sign off.
  useEffect(() => {
    if (userId) {
      void identifyUser({ id: userId, identityProvider: "blidz-account" });
    } else {
      void identifyUser({ id: generateDeviceId(), identityProvider: "blidz-device" });
    }
  }, [userId, identifyUser]);

  return <GpcConfirmation />;
}

/**
 * Visible confirmation shown when the browser's Global Privacy Control signal is honored
 * (brief R6). c15t applies GPC as an opt-out automatically; this only reports it to the user.
 */
function GpcConfirmation() {
  const [dismissed, setDismissed] = useState(false);
  const [gpcActive, setGpcActive] = useState(false);

  useEffect(() => {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
    setGpcActive(nav.globalPrivacyControl === true);
  }, []);

  if (!gpcActive || dismissed) {
    return null;
  }

  return (
    <div
      role="status"
      id="consent-gpc-confirmation"
      className="border-border bg-card text-card-foreground fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-3 border-t px-4 py-3 text-sm shadow-lg"
    >
      <span className="text-muted-foreground">
        We detected your browser&apos;s Global Privacy Control signal and turned off non-essential tracking.
      </span>
      <Button
        variant="ghost"
        size="tiny"
        id="consent-gpc-confirmation-dismiss"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
      >
        <X />
      </Button>
    </div>
  );
}

/**
 * The consent prompt, shown on every visit until the user chooses (APP-1182).
 *
 * Deliberately NOT a blocking modal. Prior blocking is enforced in code — every category stays
 * denied until an explicit choice — so an overlay buys compliance nothing and only costs conversion
 * on a storefront. `scrollLock` is therefore left off: in c15t that single prop controls both the
 * overlay and the page scroll lock. The compound form is used instead of the one-line
 * `<ConsentBanner />` for one reason — the close control, which has no prop.
 */
function ConsentPrompt() {
  const { setActiveUI, activeUI } = useConsentManager();
  const recorded = useRef(false);

  // Counsel requires the consent log to show when the prompt was first displayed, to compare against
  // consent_tracking_activated — cases have turned on gaps of seconds between the two. `notice_version`
  // is what ties the record back to the copy this visitor actually saw; `gpc` is part of the field set
  // counsel signed off on. One record per page load; "first" is min(event_time) at query time.
  useEffect(() => {
    if (activeUI !== "banner" || recorded.current) {
      return;
    }
    recorded.current = true;
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
    trackWebEvent({
      event_type: "consent_prompt_shown",
      notice_version: NOTICE_VERSION,
      gpc: nav.globalPrivacyControl === true,
    });
  }, [activeUI]);

  return (
    <ConsentBanner.Root uiSource={BANNER_UI_SOURCE}>
      <ConsentBanner.Card>
        {/*
          Closing is not a choice: no consent is stored, every category stays denied, and the prompt
          comes back on the next visit because c15t still sees no consent record.
        */}
        <Button
          variant="ghost"
          size="tiny"
          id="consent-banner-close"
          aria-label="Close without choosing"
          onClick={() => setActiveUI("none")}
          className="absolute top-2 right-2"
        >
          <X />
        </Button>
        <ConsentBanner.Header>
          <ConsentBanner.Title />
          <ConsentBanner.Description />
        </ConsentBanner.Header>
        {/* `consentAction` is what binds each button to its `theme.consentActions` entry; c15t's own
            default composition passes it, and without it these render with the base treatment. */}
        <ConsentBanner.Footer>
          <ConsentBanner.CustomizeButton consentAction="customize" />
          <ConsentBanner.FooterSubGroup>
            <ConsentBanner.RejectButton consentAction="reject" />
            <ConsentBanner.AcceptButton consentAction="accept" />
          </ConsentBanner.FooterSubGroup>
        </ConsentBanner.Footer>
      </ConsentBanner.Card>
    </ConsentBanner.Root>
  );
}

export function ConsentManager({ children }: { children: React.ReactNode }) {
  return (
    <ConsentManagerProvider options={consentOptions}>
      <ConsentPrompt />
      <ConsentDialog hideBranding uiSource={DIALOG_UI_SOURCE} />
      {children}
      <ConsentEffects />
    </ConsentManagerProvider>
  );
}
